import voiceService from '@services/voice.service';
import { hasCountryCode } from '@/lib/phone';
import { getAccessToken } from '@/lib/tokenStorage';
import { useVoiceAgentStore, type OutgoingCall } from '@stores/voiceAgentStore';
import { BridgeCall } from './bridge/BridgeCall';

/**
 * The call this agent placed. Kept outside any component for the same reason as
 * BridgePhone's: the layout is mounted again now and then, and that must not end
 * a call.
 */
const callHolder: { current: BridgeCall | null } = { current: null };

/** Tests only: forget (and end) whatever call a previous test left. */
export function resetOutboundCallForTests(): void {
  callHolder.current?.disconnect();
  callHolder.current = null;
}

export function currentOutboundCallId(): string | null {
  return callHolder.current?.sessionId ?? null;
}

export function endHeldOutboundCall(): void {
  callHolder.current?.disconnect();
  callHolder.current = null;
}

const FINISHED = new Set(['completed', 'busy', 'no_answer', 'failed', 'cancelled']);

/** Why this agent can't call this number right now, or null when they can. */
export function callBlockedReason(state: {
  phone: string;
  isOnline: boolean;
  onCall: boolean;
  mediaUrl: string | null;
}): string | null {
  if (!hasCountryCode(state.phone)) return 'Add a country code to this number before calling.';
  if (state.onCall) return 'Finish your current call first.';
  if (!state.isOnline) return 'Go online to call a customer.';
  if (!state.mediaUrl) return "Your phone isn't ready yet. Try again in a moment.";
  return null;
}

/**
 * The number a keypad entry stands for, or null until it's a whole one. A number
 * typed with its own + needs no country picked; otherwise one must be: none is
 * ever assumed.
 */
export function dialledNumber(countryCode: string, entry: string): string | null {
  const digits = entry.replace(/\D/g, '');
  if (!digits) return null;
  if (entry.trim().startsWith('+')) return `+${digits}`;
  return countryCode ? `${countryCode}${digits}` : null;
}

/** What to show while the customer hasn't answered. Exotel doesn't say when it starts ringing. */
export function outgoingCallLabel(outgoing: OutgoingCall): string {
  return outgoing.state === 'customer_ringing' ? 'Ringing…' : 'Calling…';
}

/** What to tell the agent about a call that ended unanswered. */
export function unansweredNotice(state: string): string {
  switch (state) {
    case 'busy':
      return 'The customer is busy. You can try again later.';
    case 'no_answer':
      return "The customer didn't answer. You can try again later.";
    case 'cancelled':
      return 'The call was cancelled.';
    case 'failed':
      return "The call couldn't be connected. Please try again.";
    default:
      return 'The call ended before the customer answered.';
  }
}

function microphoneFailure(error: unknown): string {
  const name = (error as { name?: string } | undefined)?.name;
  if (name === 'NotFoundError') return 'No call was placed: no microphone was found.';
  return 'No call was placed: your microphone is blocked. Allow it for this site in the address bar.';
}

/** The server's reason for refusing, which is written for the agent. */
function refusalOf(error: unknown): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : "The call couldn't be placed. Please try again.";
}

/** Put this call on screen and join its audio. Also how a call comes back after a page reload. */
function joinCall(call: { callId: string; state: string; answeredAt?: string; ticketId?: string; customerPhone: string }): void {
  const store = useVoiceAgentStore.getState();
  const mediaUrl = import.meta.env.VITE_VOICE_MEDIA_URL || store.mediaUrl;
  const token = getAccessToken();
  if (!mediaUrl || !token) throw new Error('no media address');

  const bridge = new BridgeCall(call.callId, call.callId, { mediaUrl, token, outbound: true });
  bridge.on('error', () => {
    useVoiceAgentStore.getState().setNotice(
      "Your audio couldn't connect. If the customer answers, they'll be told we lost the connection.",
    );
  });
  bridge.on('disconnect', () => {
    if (callHolder.current === bridge) callHolder.current = null;
    const state = useVoiceAgentStore.getState();
    // Ended before any update said how: the screen shouldn't just vanish.
    if (state.activeCall === bridge && !state.outgoingCall?.answeredAt && !state.notice) {
      state.setNotice(unansweredNotice('ended'));
    }
    if (state.activeCall === bridge) state.setActiveCall(null);
    if (state.callContext?.sessionId === call.callId) useVoiceAgentStore.setState({ callContext: null });
  });

  callHolder.current = bridge;
  useVoiceAgentStore.setState({
    activeCall: bridge,
    outgoingCall: { callId: call.callId, state: call.state, answeredAt: call.answeredAt },
    callContext: { customerPhone: call.customerPhone, ticketId: call.ticketId, sessionId: call.callId },
    callLive: !!call.answeredAt,
    callNotes: '',
    wrapUp: null,
    notice: null,
  });
  void bridge.accept();
}

/**
 * Call the customer on a ticket. The microphone comes first: if it's refused, no
 * call is placed and nobody's phone rings. Rejects with a message for the agent.
 */
export async function placeOutboundCall(input: { ticketId?: string; customerPhone: string }): Promise<void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    throw new Error(microphoneFailure(error));
  }

  let started;
  try {
    const key = crypto.randomUUID();
    started = input.ticketId
      ? await voiceService.startOutboundCall(input.ticketId, key)
      : await voiceService.dialOutboundCall(input.customerPhone, key);
  } catch (error) {
    throw new Error(refusalOf(error));
  }

  // A repeat of a call that has already ended, which the server returned as it was.
  if (FINISHED.has(started.state)) throw new Error(unansweredNotice(started.state));
  joinCall({ callId: started.callId, state: started.state, ticketId: input.ticketId, customerPhone: input.customerPhone });
}

/** Where the call has got to, from a live update or a poll. Repeats change nothing. */
export function applyOutboundUpdate(update: { callId?: string; state?: string; answeredAt?: string }): void {
  const bridge = callHolder.current;
  if (!bridge || !update.state || update.callId !== bridge.sessionId) return;
  const store = useVoiceAgentStore.getState();
  const outgoing = store.outgoingCall;
  if (!outgoing || outgoing.callId !== update.callId) return;

  if (FINISHED.has(update.state)) {
    if (!outgoing.answeredAt) store.setNotice(unansweredNotice(update.state));
    bridge.disconnect();
    return;
  }

  const answered = update.state === 'in_progress' || !!update.answeredAt;
  store.setOutgoingCall({
    ...outgoing,
    state: update.state,
    answeredAt: outgoing.answeredAt ?? (answered ? update.answeredAt ?? new Date().toISOString() : undefined),
  });
  if (answered) store.setCallLive(true);
}

/** Bring back this agent's call after a page reload, if it's still going. */
export async function recoverOutboundCall(): Promise<void> {
  if (callHolder.current || useVoiceAgentStore.getState().activeCall) return;
  const call = await voiceService.getActiveOutboundCall();
  if (!call || callHolder.current || useVoiceAgentStore.getState().activeCall) return;
  joinCall({
    callId: call.callId,
    state: call.state,
    answeredAt: call.answeredAt,
    ticketId: call.ticketId,
    customerPhone: call.customerNumber,
  });
}
